import type { SupabaseClient } from '@supabase/supabase-js';
import type { Dish, DishInput, MenuSnapshot, SnapshotItem } from '@lemontaigu/shared';
import type { MenuStore } from './menuStore.js';
import type { DishEmbed, FutureServing } from './transform.js';
import { inputToRow, rowToDish } from './transform.js';

/**
 * Production `MenuStore` adapter — the only module that talks to Supabase for
 * menu data. Bodies are the former data/* function bodies verbatim, with
 * `supabase` → `this.sb`. Everything else depends on the `MenuStore` port.
 */
export class SupabaseMenuStore implements MenuStore {
  constructor(private readonly sb: SupabaseClient) {}

  async listDishes(): Promise<Dish[]> {
    const { data, error } = await this.sb
      .from('dishes')
      .select('*')
      .is('deleted_at', null)
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []).map(rowToDish);
  }

  async createDish(input: DishInput): Promise<Dish> {
    const { data, error } = await this.sb
      .from('dishes')
      .insert(inputToRow(input))
      .select('*')
      .single();
    if (error) throw new Error(error.message);
    return rowToDish(data);
  }

  async updateDish(id: number, patch: Partial<DishInput>): Promise<Dish> {
    const { data, error } = await this.sb
      .from('dishes')
      .update(inputToRow(patch))
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw new Error(error.message);
    return rowToDish(data);
  }

  async softDeleteDish(id: number): Promise<void> {
    const { error } = await this.sb
      .from('dishes')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  }

  async bulkSetActive(ids: number[], active: boolean): Promise<Dish[]> {
    if (!ids.length) return [];
    const { data, error } = await this.sb
      .from('dishes')
      .update({ is_active: active })
      .in('id', ids)
      .select('*');
    if (error) throw new Error(error.message);
    return (data ?? []).map(rowToDish);
  }

  async panelDishes(dateIso: string): Promise<Dish[]> {
    const { data, error } = await this.sb
      .from('dishes')
      .select('*, dish_servings!inner(date)')
      .eq('dish_servings.date', dateIso)
      .eq('is_active', true)
      .is('deleted_at', null)
      .contains('menu_types', ['daily']);
    if (error) throw new Error(error.message);
    return (data ?? []).map(rowToDish);
  }

  async serveDish(dishId: number, dateIso: string): Promise<void> {
    const { error } = await this.sb
      .from('dish_servings')
      .upsert({ dish_id: dishId, date: dateIso }, { onConflict: 'dish_id,date', ignoreDuplicates: true });
    if (error) throw new Error(error.message);
  }

  async unserveDish(dishId: number, dateIso: string): Promise<void> {
    const { error } = await this.sb
      .from('dish_servings')
      .delete()
      .eq('dish_id', dishId)
      .eq('date', dateIso);
    if (error) throw new Error(error.message);
  }

  async reproduceDay(srcDate: string, toDate: string): Promise<number> {
    const { data, error } = await this.sb.rpc('reproduce_day', { src_date: srcDate, to_date: toDate });
    if (error) throw new Error(error.message);
    return (data as number | null) ?? 0;
  }

  async setDayServings(dateIso: string, dishIds: number[]): Promise<number> {
    const { data, error } = await this.sb.rpc('set_day_servings', { p_date: dateIso, p_dish_ids: dishIds });
    if (error) throw new Error(error.message);
    return (data as number | null) ?? 0;
  }

  async listHistory(limit = 90): Promise<MenuSnapshot[]> {
    const { data, error } = await this.sb
      .from('menu_snapshots')
      .select('date, items')
      .order('date', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({
      date: r.date as string,
      items: (r.items ?? []) as SnapshotItem[],
    }));
  }

  async listFutureServings(today: string): Promise<FutureServing[]> {
    const { data, error } = await this.sb
      .from('dish_servings')
      .select('date, dishes!inner(*)')
      .gte('date', today)
      .order('date', { ascending: true });
    if (error) throw new Error(error.message);
    // dish_servings → dishes is a to-one FK (PostgREST returns a single object);
    // supabase-js infers an array without generated DB types, so cast via unknown.
    return ((data ?? []) as unknown as Array<{ date: string; dishes: DishEmbed }>).map((r) => ({
      date: r.date,
      dish: r.dishes,
    }));
  }
}
